import React, { useEffect } from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';
import {
    VerificationService,
    TeacherPersonalInfoViewModel,
    TeacherPersonalInfoResponse,
    hasFailedInstantMatch,
    FormSelectChoice,
    getAvailableLocaleChoices,
    LogoComponent,
    ChangeLocaleComponent,
    TeacherSchoolComponent,
    Organization,
    updateViewModelOrganization,
    updateFieldValidationErrorsByFieldId,
    handleEmailOnKeyDown,
    shouldCollectPostalCode,
    submitForm,
    VerificationStepsEnum,
    isFormFilled,
    setRef,
    EmailComponent,
    PhoneNumberComponent,
    HowDoesVerifyingWorkComponent,
    FirstNameComponent,
    FormFooterComponent,
    LastNameComponent,
    MarketConsentWrapper,
    CountryComponentWrapper,
    Locale,
    getSafe,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    State,
} from '@sheerid/jslib';
import { PostalCodeComponent } from '../FormFields/PostalCode/PostalCodeComponent';
import { AreaOfStudyComponent } from '../FormFields/AreaOfStudy/AreaOfStudyComponent';
import { PlanOfUseComponent } from '../FormFields/PlanOfUse/PlanOfUseComponent';
import { programId } from '../../programOptions';
import { LinkedInUrlComponent } from '../FormFields/LinkedInURL/LinkedInUrlComponent';
import { RegionSelectComponent } from '../FormInputs/RegionSelectComponent';


interface StepTeacherPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepTeacherPersonalInfo = ({ intl, verificationService }: StepTeacherPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as TeacherPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as TeacherPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };

    const updateTeacherViewModel = (key: keyof TeacherPersonalInfoViewModel, value: any) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            (draft[key] as any) = value;
            if (draft.countryChoice) {
                draft.metadata = { ...draft.metadata, country: draft.countryChoice.value };
            }
        });
        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: TeacherPersonalInfoViewModel = produce(viewModel, (draft: TeacherPersonalInfoViewModel) => {
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    // Don't validate DOB if ID Check is not enabled
    useEffect(() => {
        if (!verificationService.programTheme.idCheckAddOnEnabled) {
            updateTeacherViewModel('birthDate', '');
            updateTeacherViewModel('fieldsToSkipValidation', ['birthDate']);
        } else {
            updateTeacherViewModel('fieldsToSkipValidation', []);
        }
    }, [verificationService.programTheme.idCheckAddOnEnabled]);

    return (
        <div id="sid-step-teacher-personal-info" className="sid-l-container">


            {
                failedInstantMatch
                    ? (
                        <div className="sid-header">
                            <div className="sid-l-horz-center">
                                <LogoComponent verificationService={verificationService} />
                            </div>
                            <div className="sid-header__title sid-l-horz-center">
                                <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Email" />
                            </div>
                            <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                                <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle" defaultMessage="Your full name and email address must match what is on record with your school." />
                            </div>
                        </div>
                    ) : (
                        <div className="sid-header">
                            <div className="sid-l-horz-center">
                                <LogoComponent verificationService={verificationService} />
                            </div>
                            <div className="sid-header__title sid-l-horz-center">
                                <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Teaching Staff-Only Offer" />
                            </div>
                            <div className="sid-header__subtitle sid-l-horz-center">
                                <FormattedHTMLMessage id="step.personalInfo.subtitle" defaultMessage="Verify you're a teacher, faculty or staff at a K-12 or degree-granting university." />
                                &nbsp;
                                <HowDoesVerifyingWorkComponent verificationService={verificationService} />
                            </div>
                        </div>
                    )
            }

            {
                localeChoices.length > 1
                    ? (
                        <ChangeLocaleComponent
                            options={localeChoices}
                            value={viewModel.localeChoice || defaultLocaleChoice}
                            isErrored={false}
                            onChange={(localeChoice: FormSelectChoice<Locale, string>) => updateLocale(localeChoice)}
                        />
                    ) : null
            }

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField="organization"
                viewModelDraftDecorator={(draft, countryChoice) => {
                    const viewModelDraft = draft as TeacherPersonalInfoViewModel;
                    const country = countryChoice ? countryChoice.value : undefined;

                    if (viewModel.countryChoice?.value !== country) {
                        viewModelDraft.metadata.region = '';
                    }
                }}
            />

            <RegionSelectComponent
                verificationService={verificationService}
                country={viewModel.countryChoice?.value}
                value={viewModel.metadata?.region}
                onChange={
                    (newValue: FormSelectChoice<State, string>) => {
                        updateTeacherViewModel('metadata', {
                            ...verificationService.viewModel.metadata,
                            region: newValue?.value.toString() || '',
                        });
                        updateFieldValidationErrorsByFieldId('region', newValue?.value, verificationService);
                    }
                }
                isRequired
                isErrored={!!fieldValidationErrors.region}
            />

            <TeacherSchoolComponent
                value={(verificationService.viewModel as TeacherPersonalInfoViewModel).organization}
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.organization}
                placeholder={
                    intl.formatMessage({
                        id: 'school',
                        defaultMessage: 'Name of College/University',
                    })
                }
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
            />

            <div className="sid-personal-information">
                <FormattedHTMLMessage
                    id="personalInformation"
                    defaultMessage="Personal information"
                />
            </div>

            <FirstNameComponent
                value={viewModel.firstName}
                isErrored={!!fieldValidationErrors.firstName}
                placeholder={
                    intl.formatMessage({
                        id: 'firstName',
                        defaultMessage: 'Legal First Name',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }
                }
            />

            <LastNameComponent
                value={viewModel.lastName}
                isErrored={!!fieldValidationErrors.lastName}
                placeholder={
                    intl.formatMessage({
                        id: 'lastName',
                        defaultMessage: 'Legal Last Name',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }
                }
            />

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Must be your school-issued email address"
                    />
                )}
                placeholder={
                    intl.formatMessage({
                        id: 'email',
                        defaultMessage: 'School - Issued Email Address',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled
                    ? (
                        <PhoneNumberComponent
                            isRequired={!!verificationService.programTheme.smsLoopEnabled}
                            value={viewModel.phoneNumber}
                            internationalSupport
                            isErrored={!!fieldValidationErrors.phoneNumber}
                            onChange={
                                (newValue: string) => {
                                    updateTeacherViewModel('phoneNumber', newValue);
                                    updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                                }
                            }
                        />
                    ) : null
            }

            {
                shouldCollectPostalCode(verificationService.programTheme, viewModel)
                    ? (
                        <PostalCodeComponent
                            isErrored={!!fieldValidationErrors.postalCode}
                            onChange={(newValue: string) => {
                                updateTeacherViewModel('postalCode', newValue);
                                updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                            }}
                            value={viewModel.postalCode}
                            placeholder={
                                intl.formatMessage({
                                    id: 'postalCodePlaceholder',
                                    defaultMessage: 'ZIP Code or Postal Code',
                                })
                            }
                        />
                    ) : null
            }

            <div className="sid-field__label sid-additional-info">
                <FormattedHTMLMessage id="additionalInfoTitle" defaultMessage="Additional Information:" />
            </div>

            <AreaOfStudyComponent
                isErrored={!!fieldValidationErrors['area-of-study']}
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('metadata', { ...verificationService.viewModel.metadata, 'area-of-study': newValue });
                        updateFieldValidationErrorsByFieldId('area-of-study', newValue, verificationService);
                    }
                }
                value={viewModel.metadata['area-of-study']}
            />

            <PlanOfUseComponent
                value={getSafe(() => viewModel.metadata['plan-of-use'], '') as string}
                isErrored={!!fieldValidationErrors['plan-of-use']}
                placeholder={
                    intl.formatMessage({
                        id: 'planOfUse',
                        defaultMessage: 'How do you plan to use Alteryx in your classroom?',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('metadata', { ...verificationService.viewModel.metadata, 'plan-of-use': newValue });
                        updateFieldValidationErrorsByFieldId('plan-of-use', newValue, verificationService);
                    }
                }
            />

            <LinkedInUrlComponent
                value={getSafe(() => viewModel.metadata['linked-in-url'], '') as string}
                placeholder={
                    intl.formatMessage({
                        id: 'linkedInUrl',
                        defaultMessage: 'Personal LinkedIn URL (optional)',
                    })
                }
                onChange={
                    (newValue: string) => {
                        updateTeacherViewModel('metadata', { ...verificationService.viewModel.metadata, 'linked-in-url': newValue });
                        updateFieldValidationErrorsByFieldId('linked-in-url', newValue, verificationService);
                    }
                }
            />

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateTeacherViewModel('metadata', { marketConsentValue: newValue, 'research-use': newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />


            <div className="sid-footer__one sid-l-space-top-md">
                <FormattedHTMLMessage
                    id="optInFutureCommunication"
                    defaultMessage="By submitting this form, I am opting in to receive future communications from SparkED education program."
                />
            </div>

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => {
                            submitForm(viewModel, verificationService, VerificationStepsEnum.collectTeacherPersonalInfo);
                        }}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-label="submit"
                        ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        {
                            failedInstantMatch
                                ? <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                                : <FormattedHTMLMessage id="verifyMyTeacherStatus" defaultMessage="Verify My Teacher Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooterComponent verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="cantFindSchool"
                    defaultMessage="If your school is not in the list, please contact SheerID directly. Please send the <a class='sid-link' href='https://offers.sheerid.com/sheerid/add-school-request/?pid={pid}'>request form</a> after filling in the necessary items. If your school is added in the list, you will get the email notification from SheerID."
                    values={{
                        pid: programId,
                    }}
                />
                <div className="sid-footer sid-l-space-top-md">
                    <FormattedHTMLMessage
                        id="SparkEDFaq"
                        defaultMessage="Please visit our <a href=https://s3.us-east-2.amazonaws.com/alteryx.sparked/Documentation/SparkED+Getting+Started+FAQ.pdf target=“_blank”>SparkED: Getting Started FAQ</a> to learn about receiving and activating your Alteryx license key."
                    />
                </div>
            </FormFooterComponent>
        </div>
    );
};

export const StepTeacherPersonalInfoComponent = injectIntl(StepTeacherPersonalInfo);
